import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { getAPIBaseURL } from '../../services/helpers'
import { postData, getData, updateData } from '../../services/request'
import { listEvents, createEvent } from '../../services/events'
import { uploadEventImage } from '../../services/upload'
import { listCenterOrders } from '../../services/orders'
import { getWalletBalance, getWalletTransactions } from '../../services/wallet'
import { listCenterTicketTypes, addTicketType, updateTicketType, deleteTicketType } from '../../services/ticketTypes'
import { listCenterTickets } from '../../services/tickets'
import {
  CalendarIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  PlusCircleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  PhotoIcon,
  TagIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CreditCardIcon,
  TicketIcon as TicketIconOutline,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  Bars3Icon,
  HomeModernIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import Logo from '../../asset/images/logo.svg'

const CenterDashboardPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  
  const [activeTab, setActiveTab] = useState('overview')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Profile state
  const [profile, setProfile] = useState({
    organization: user?.organization || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    country: user?.country || '',
    city: user?.city || '',
    address: user?.address || '',
    accountNumber: user?.accountNumber || '',
    accountName: user?.accountName || '',
    bank: user?.bank || ''
  })
  
  // Wallet state
  const [walletBalance, setWalletBalance] = useState(0)
  const [walletTransactions, setWalletTransactions] = useState([])
  const [loadingWallet, setLoadingWallet] = useState(true)
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('ALL')
  
  // Event creation state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    venue: '',
    location: {
      city: '',
      country: ''
    },
    startsAt: '',
    endsAt: '',
    sessions: [],
    ranks: [],
    slots: '',
    coverImage: '',
    status: 'DRAFT',
    publishAt: '',
    discounts: [],
    tags: ''
  })
  
  // Session form state
  const [sessionForm, setSessionForm] = useState({
    name: '',
    startsAt: '',
    endsAt: ''
  })
  
  // Discount form state
  const [discountForm, setDiscountForm] = useState({
    type: 'PERCENT',
    value: '',
    expiresAt: ''
  })
  
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  
  // Dashboard stats
  const [stats, setStats] = useState({
    upcomingEvents: 0,
    ticketsSoldToday: 0,
    walletBalance: 0
  })
  
  const [events, setEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [orders, setOrders] = useState([])
  const [loadingOrders, setLoadingOrders] = useState(true)
  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL')

  // Tickets state
  const [ticketTypes, setTicketTypes] = useState([])
  const [loadingTicketTypes, setLoadingTicketTypes] = useState(true)
  const [tickets, setTickets] = useState([])
  const [loadingTickets, setLoadingTickets] = useState(true)
  const [ticketEventFilter, setTicketEventFilter] = useState('ALL')
  const [ticketStatusFilter, setTicketStatusFilter] = useState('ALL')
  const [showTicketTypeForm, setShowTicketTypeForm] = useState(false)
  const [editingTicketType, setEditingTicketType] = useState(null)
  const [ticketTypeForm, setTicketTypeForm] = useState({
    eventId: '',
    name: '',
    rank: '',
    session: '',
    price: '',
    currency: 'NGN',
    serviceFee: '0',
    quantity: '',
    discounts: [],
    salesWindow: { startsAt: '', endsAt: '' }
  })

  useEffect(() => {
    const currentUser = getUser()
    if (currentUser) {
      setUser(currentUser)
      document.title = `${currentUser?.organization || currentUser?.name || 'Event Center'} - Obule Dashboard`
      fetchEvents()
      fetchOrders()
      fetchWallet()
      fetchStats()
      fetchTicketTypes()
      fetchTickets()
    } else {
      navigate('/center/login')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchEvents = async () => {
    try {
      setLoadingEvents(true)
      const currentUser = getUser()
      const userId = currentUser?._id || currentUser?.id
      if (userId) {
        const res = await listEvents({ centerId: userId, status: 'ALL' })
        if (res.status === 200) {
          const eventsList = Array.isArray(res.data) ? res.data : []
          setEvents(eventsList)
          setStats(prev => ({ ...prev, upcomingEvents: eventsList.length }))
        } else {
          setEvents([])
        }
      } else {
        setEvents([])
      }
      setLoadingEvents(false)
    } catch (error) {
      console.error('Error fetching events:', error)
      setEvents([])
      setLoadingEvents(false)
    }
  }

  const fetchStats = async () => {
    try {
      // Calculate tickets sold today from tickets
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      // Fetch all tickets (no filters) to calculate today's sales
      const ticketsRes = await listCenterTickets({})
      let ticketsSoldToday = 0
      
      if (ticketsRes.status === 200 && Array.isArray(ticketsRes.data)) {
        ticketsSoldToday = ticketsRes.data.filter(ticket => {
          if (!ticket.issuedAt) return false
          const issuedDate = new Date(ticket.issuedAt)
          return issuedDate >= today && issuedDate < tomorrow
        }).length
      }
      
      // Get wallet balance
      const balanceRes = await getWalletBalance()
      const balance = balanceRes.status === 200 ? (balanceRes.data.balance || 0) : 0
      
      // Update stats
      setStats(prev => ({
        ...prev,
        ticketsSoldToday,
        walletBalance: balance
      }))
    } catch (error) {
      console.error('Error fetching stats:', error)
      // Keep existing wallet balance on error, just reset tickets sold today
      setStats(prev => ({
        ...prev,
        ticketsSoldToday: 0
      }))
    }
  }

  const fetchWallet = async () => {
    try {
      setLoadingWallet(true)
      const balanceRes = await getWalletBalance()
      if (balanceRes.status === 200) {
        setWalletBalance(balanceRes.data.balance || 0)
      }
      
      const transactionsRes = await getWalletTransactions({ limit: 100 })
      if (transactionsRes.status === 200) {
        const transactions = transactionsRes.data.transactions || []
        setWalletTransactions(transactions)
      }
      setLoadingWallet(false)
    } catch (error) {
      console.error('Error fetching wallet:', error)
      setLoadingWallet(false)
    }
  }


  const fetchOrders = async () => {
    try {
      setLoadingOrders(true)
      const res = await listCenterOrders()
      if (res.status === 200) {
        const ordersList = Array.isArray(res.data) ? res.data : []
        setOrders(ordersList)
      } else {
        setOrders([])
      }
      setLoadingOrders(false)
    } catch (error) {
      console.error('Error fetching orders:', error)
      setOrders([])
      setLoadingOrders(false)
    }
  }

  const fetchTicketTypes = async () => {
    try {
      setLoadingTicketTypes(true)
      const res = await listCenterTicketTypes()
      if (res.status === 200) {
        const ticketTypesList = Array.isArray(res.data) ? res.data : []
        setTicketTypes(ticketTypesList)
      } else {
        setTicketTypes([])
      }
      setLoadingTicketTypes(false)
    } catch (error) {
      console.error('Error fetching ticket types:', error)
      setTicketTypes([])
      setLoadingTicketTypes(false)
    }
  }

  const fetchTickets = async () => {
    try {
      setLoadingTickets(true)
      const filters = {}
      if (ticketEventFilter !== 'ALL') filters.eventId = ticketEventFilter
      if (ticketStatusFilter !== 'ALL') filters.status = ticketStatusFilter
      const res = await listCenterTickets(filters)
      if (res.status === 200) {
        const ticketsList = Array.isArray(res.data) ? res.data : []
        setTickets(ticketsList)
        
        // Update tickets sold today stat if on overview tab
        if (activeTab === 'overview') {
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const tomorrow = new Date(today)
          tomorrow.setDate(tomorrow.getDate() + 1)
          
          const ticketsSoldToday = ticketsList.filter(ticket => {
            if (!ticket.issuedAt) return false
            const issuedDate = new Date(ticket.issuedAt)
            return issuedDate >= today && issuedDate < tomorrow
          }).length
          
          setStats(prev => ({ ...prev, ticketsSoldToday }))
        }
      } else {
        setTickets([])
      }
      setLoadingTickets(false)
    } catch (error) {
      console.error('Error fetching tickets:', error)
      setTickets([])
      setLoadingTickets(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketEventFilter, ticketStatusFilter])

  useEffect(() => {
    // Refresh stats when switching to overview tab
    if (activeTab === 'overview') {
      fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const handleProfileChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setProfile(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleEventChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setEventForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setEventForm(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSessionChange = (e) => {
    const { name, value } = e.target
    setSessionForm(prev => ({ ...prev, [name]: value }))
  }

  const addSession = () => {
    if (sessionForm.name && sessionForm.startsAt && sessionForm.endsAt) {
      setEventForm(prev => ({
        ...prev,
        sessions: [...prev.sessions, {
          name: sessionForm.name,
          startsAt: new Date(sessionForm.startsAt).toISOString(),
          endsAt: new Date(sessionForm.endsAt).toISOString()
        }]
      }))
      setSessionForm({ name: '', startsAt: '', endsAt: '' })
    }
  }

  const removeSession = (index) => {
    setEventForm(prev => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index)
    }))
  }

  const handleRankChange = (e) => {
    const value = e.target.value
    if (value && !eventForm.ranks.includes(value)) {
      setEventForm(prev => ({
        ...prev,
        ranks: [...prev.ranks, value]
      }))
      e.target.value = ''
    }
  }

  const removeRank = (index) => {
    setEventForm(prev => ({
      ...prev,
      ranks: prev.ranks.filter((_, i) => i !== index)
    }))
  }

  const handleDiscountChange = (e) => {
    const { name, value } = e.target
    setDiscountForm(prev => ({ ...prev, [name]: value }))
  }

  const addDiscount = () => {
    if (discountForm.type && discountForm.value) {
      setEventForm(prev => ({
        ...prev,
        discounts: [...prev.discounts, {
          type: discountForm.type,
          value: Number(discountForm.value),
          expiresAt: discountForm.expiresAt ? new Date(discountForm.expiresAt).toISOString() : undefined
        }]
      }))
      setDiscountForm({ type: 'PERCENT', value: '', expiresAt: '' })
    }
  }

  const removeDiscount = (index) => {
    setEventForm(prev => ({
      ...prev,
      discounts: prev.discounts.filter((_, i) => i !== index)
    }))
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB')
      return
    }

    setImageFile(file)
    setError('')

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)

    setUploadingImage(true)
    try {
      const res = await uploadEventImage(file)
      if (res.status === 200 && res.data?.imageUrl) {
        setEventForm(prev => ({ ...prev, coverImage: res.data.imageUrl }))
        setSuccess('Image uploaded successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to upload image')
        setImageFile(null)
        setImagePreview(null)
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.')
      setImageFile(null)
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')
    
    try {
      const url = `${getAPIBaseURL()}/v1/users/update-account`
      const token = localStorage.getItem('token')
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...(token ? { 'token': token } : {})
      }
      const params = new URLSearchParams()
      
      Object.keys(profile).forEach(key => {
        if (key === 'location' && typeof profile[key] === 'object') {
          Object.keys(profile[key]).forEach(subKey => {
            params.append(`location.${subKey}`, profile[key][subKey] || '')
          })
        } else if (profile[key] !== undefined && profile[key] !== null && profile[key] !== '') {
          params.append(key, profile[key])
        }
      })
      
      const res = await updateData(url, headers, params.toString())
      
      if (res.error === '') {
        if (res.result.status === 200) {
          setSuccess('Profile updated successfully!')
          const updatedUserData = res.result.data?.data || res.result.data
          const updatedUser = { ...user, ...profile, ...updatedUserData }
          localStorage.setItem('user', JSON.stringify(updatedUser))
          setUser(updatedUser)
          setTimeout(() => setSuccess(''), 3000)
        } else {
          setError(res.result?.data?.data || res.result?.data?.message || 'Failed to update profile')
        }
      } else {
        setError(res.result?.data?.data || res.result?.data?.message || 'Failed to update profile')
      }
    } catch (err) {
      setError('An error occurred while updating profile')
    } finally {
      setLoading(false)
    }
  }

  const handleEventSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')
    
    try {
      const params = new URLSearchParams()
      
      params.append('title', eventForm.title)
      params.append('description', eventForm.description)
      params.append('venue', eventForm.venue)
      params.append('location.city', eventForm.location.city || '')
      params.append('location.country', eventForm.location.country || '')
      params.append('startsAt', new Date(eventForm.startsAt).toISOString())
      params.append('endsAt', new Date(eventForm.endsAt).toISOString())
      params.append('slots', eventForm.slots)
      params.append('status', eventForm.status || 'DRAFT')
      if (eventForm.coverImage) params.append('coverImage', eventForm.coverImage)
      if (eventForm.publishAt) params.append('publishAt', new Date(eventForm.publishAt).toISOString())
      if (eventForm.sessions && eventForm.sessions.length > 0) {
        eventForm.sessions.forEach((session, index) => {
          params.append(`sessions[${index}].name`, session.name)
          params.append(`sessions[${index}].startsAt`, session.startsAt)
          params.append(`sessions[${index}].endsAt`, session.endsAt)
        })
      }
      if (eventForm.ranks && eventForm.ranks.length > 0) {
        eventForm.ranks.forEach(rank => {
          params.append('ranks[]', rank)
        })
      }
      if (eventForm.discounts && eventForm.discounts.length > 0) {
        eventForm.discounts.forEach((discount, index) => {
          params.append(`discounts[${index}].type`, discount.type)
          params.append(`discounts[${index}].value`, discount.value)
          if (discount.expiresAt) params.append(`discounts[${index}].expiresAt`, discount.expiresAt)
        })
      }
      if (eventForm.tags) {
        eventForm.tags.split(',').forEach(tag => {
          if (tag.trim()) params.append('tags[]', tag.trim())
        })
      }
      
      const res = await createEvent(params.toString())
      
      if (res.status === 201) {
        setSuccess('Event created successfully!')
        setEventForm({
          title: '',
          description: '',
          venue: '',
          location: { city: '', country: '' },
          startsAt: '',
          endsAt: '',
          sessions: [],
          ranks: [],
          slots: '',
          coverImage: '',
          status: 'DRAFT',
          publishAt: '',
          discounts: [],
          tags: ''
        })
        setSessionForm({ name: '', startsAt: '', endsAt: '' })
        setDiscountForm({ type: 'PERCENT', value: '', expiresAt: '' })
        setImageFile(null)
        setImagePreview(null)
        fetchEvents()
        setActiveTab('events')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to create event')
      }
    } catch (err) {
      setError('An error occurred while creating event')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'events', label: 'My Events', icon: CalendarIcon, isLink: true, path: '/center/events', disabled: true }, // Special: navigates to separate page
    { id: 'offers', label: 'My Offers', icon: TagIcon, isLink: true, path: '/center/offers' }, // Special: navigates to separate page
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, isLink: true, path: '/center/orders' }, // Special: navigates to separate page
    { id: 'tickets', label: 'Tickets', icon: TicketIconOutline, isLink: true, path: '/center/tickets' }, // Special: navigates to separate page
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, isLink: true, path: '/center/media', disabled: true }, // Special: navigates to separate page
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, isLink: true, path: '/center/disputes' }, // Special: navigates to separate page
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, isLink: true, path: '/center/wallet' }, // Special: navigates to separate page
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, isLink: true, path: '/center/profile' } // Special: navigates to separate page
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
      case 'TICKET_ISSUED':
        return 'bg-green-50 text-green-700 border-green-200'
      case 'INITIATED':
        return 'bg-amber-50 text-amber-700 border-amber-200'
      case 'CANCELLED':
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200'
      case 'REFUNDED':
        return 'bg-slate-50 text-slate-700 border-slate-200'
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200'
    }
  }

  const filteredOrders = orderStatusFilter === 'ALL' 
    ? orders 
    : orders.filter(order => order.status === orderStatusFilter)

  const filteredTransactions = transactionTypeFilter === 'ALL'
    ? walletTransactions
    : walletTransactions.filter(t => t.type === transactionTypeFilter.toLowerCase())

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-1.5 group">
              <img
                src={Logo}
                alt="Obule"
                className="h-9 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
              />
              <span className="text-xl font-semibold tracking-wide text-slate-900">bule</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
              </button>
            </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              
              // Special handling for items that navigate to separate pages
              if (tab.isLink) {
                return (
                  <Link
                    key={tab.id}
                    to={tab.path}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5 text-slate-400" />
                    {tab.label}
                  </Link>
                )
              }
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    setMobileMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === tab.id
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </button>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
          </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
        </div>
      </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Dashboard</span>
          <div className="w-6" /> {/* Spacer */}
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Global Notifications */}
        {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3 animate-fade-in shadow-sm">
              <XCircleIcon className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-900"><XMarkIcon className="w-5 h-5" /></button>
          </div>
        )}
        {success && (
            <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-100 text-green-700 flex items-center gap-3 animate-fade-in shadow-sm">
              <CheckCircleIcon className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{success}</span>
              <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-900"><XMarkIcon className="w-5 h-5" /></button>
          </div>
        )}

          {/* View Title */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
            <p className="mt-1 text-slate-500">Manage your event center operations</p>
          </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Upcoming Events Card */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                      <CalendarIcon className="w-6 h-6" />
                  </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Events</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.upcomingEvents}</p>
                  <p className="text-sm text-slate-500 mt-1">Total active events</p>
              </div>

                {/* Tickets Sold Card */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                      <TicketIcon className="w-6 h-6" />
                  </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sales</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.ticketsSoldToday}</p>
                  <p className="text-sm text-slate-500 mt-1">Tickets sold today</p>
              </div>

                {/* Wallet Balance Card */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                      <WalletIcon className="w-6 h-6" />
                  </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Wallet</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">₦{stats.walletBalance.toLocaleString()}</p>
                  <p className="text-sm text-slate-500 mt-1">Available balance</p>
              </div>
            </div>

            {/* Quick Actions */}
              <h3 className="text-lg font-bold text-slate-900 mt-8 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link to="/center/offers" className="group flex items-center p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-orange-500 hover:shadow-md transition-all text-left">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 mr-4 group-hover:scale-110 transition-transform">
                    <TagIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Manage Offers</p>
                    <p className="text-xs text-slate-500">Create special deals</p>
                  </div>
                </Link>
                <button onClick={() => setActiveTab('orders')} className="group flex items-center p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-purple-500 hover:shadow-md transition-all text-left">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 mr-4 group-hover:scale-110 transition-transform">
                    <ShoppingBagIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">View Orders</p>
                    <p className="text-xs text-slate-500">Track ticket sales</p>
                  </div>
                </button>
                <Link to="/center/support" className="group flex items-center p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-blue-500 hover:shadow-md transition-all text-left">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 mr-4 group-hover:scale-110 transition-transform">
                    <Cog6ToothIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Support</p>
                    <p className="text-xs text-slate-500">Get help & docs</p>
                </div>
              </Link>
            </div>
          </div>
        )}

          {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
              <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="font-semibold text-slate-800">Your Events List</h3>
            </div>

            {loadingEvents ? (
                <div className="text-center py-20 text-slate-500">Loading events...</div>
            ) : events.length === 0 ? (
                <div className="text-center py-24 bg-white rounded-2xl border border-slate-200 border-dashed">
                  <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900">No events yet</h3>
                  <p className="text-slate-500 mb-6">View your events list to get started</p>
              </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {events.map((event) => (
                    <div key={event._id || event.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                      <div className="h-48 bg-slate-100 relative">
                        {event.coverImage ? (
                          <img src={event.coverImage} alt={event.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <PhotoIcon className="w-12 h-12" />
                      </div>
                    )}
                        <div className="absolute top-4 right-4">
                          <span className={`px-2 py-1 rounded-md text-xs font-bold shadow-sm ${event.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                            {event.status}
                      </span>
                    </div>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-1">{event.title}</h3>
                        <div className="space-y-2 text-sm text-slate-500 mb-6 flex-1">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4" />
                        {new Date(event.startsAt).toLocaleDateString()}
                      </div>
                          <div className="flex items-center gap-2">
                            <MapPinIcon className="w-4 h-4" />
                            {event.location?.city || event.venue}
                      </div>
                          {event.slots && (
                            <div className="flex items-center gap-2">
                              <UsersIcon className="w-4 h-4" />
                              {event.slots} Capacity
                            </div>
                          )}
                    </div>
                    <Link
                      to={`/center/events/${event._id || event.id}`}
                          className="w-full flex items-center justify-center py-2.5 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition font-medium border border-slate-200"
                    >
                          Manage Event
                    </Link>
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

          {/* Orders, Tickets, Wallet, Profile tabs would follow similar redesign patterns, simplifying for brevity but maintaining logic */}
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-emerald-100 text-sm font-medium">Total Orders</p>
                  <ShoppingBagIcon className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-3xl font-bold">{orders.length}</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-green-100 text-sm font-medium">Completed</p>
                  <CheckCircleIcon className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-3xl font-bold">{orders.filter(o => o.status === 'TICKET_ISSUED' || o.status === 'PAID').length}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-yellow-100 text-sm font-medium">Pending</p>
                  <ClockIcon className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-3xl font-bold">{orders.filter(o => o.status === 'INITIATED').length}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-purple-100 text-sm font-medium">Total Revenue</p>
                  <CreditCardIcon className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-3xl font-bold">
                  ₦{orders.filter(o => o.status === 'TICKET_ISSUED' || o.status === 'PAID').reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {['ALL', 'INITIATED', 'PAID', 'TICKET_ISSUED', 'CANCELLED', 'FAILED'].map(status => (
                <button
                  key={status}
                  onClick={() => setOrderStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    orderStatusFilter === status 
                      ? 'bg-emerald-600 text-white shadow-lg' 
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left">Order ID</th>
                      <th className="px-6 py-4 text-left">Type</th>
                      <th className="px-6 py-4 text-left">Event/Offer</th>
                      <th className="px-6 py-4 text-left">Date</th>
                      <th className="px-6 py-4 text-left">Customer</th>
                      <th className="px-6 py-4 text-left">Items</th>
                      <th className="px-6 py-4 text-left">Amount</th>
                      <th className="px-6 py-4 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-16 text-center">
                          <ShoppingBagIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">No orders found</p>
                          <p className="text-slate-400 text-sm mt-1">Orders will appear here when customers make purchases</p>
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map(order => (
                        <tr key={order._id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                              #{order.paymentTxRef?.slice(-8) || order._id.slice(-8)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              order.orderType === 'EVENT' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {order.orderType || 'EVENT'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-slate-900 font-medium truncate">
                                {order.eventId?.title || order.offerId?.title || 'N/A'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            <div>
                              <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                              <p className="text-xs text-slate-400">
                                {new Date(order.createdAt).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-slate-900 font-medium">{order.attendeeId?.name || 'Guest'}</p>
                              <p className="text-xs text-slate-500">{order.attendeeId?.email || ''}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            {order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0} ticket(s)
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-900 font-semibold">
                              {order.currency || 'NGN'} {order.total?.toLocaleString()}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              order.status === 'TICKET_ISSUED' || order.status === 'PAID'
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : order.status === 'INITIATED'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                : order.status === 'CANCELLED'
                                ? 'bg-gray-100 text-gray-700 border border-gray-200'
                                : 'bg-red-100 text-red-700 border border-red-200'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

          {/* Wallet Tab */}
          {activeTab === 'wallet' && (
            <div className="space-y-6">
              <div className="p-8 rounded-3xl bg-slate-900 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-12 opacity-10 transform translate-x-10 -translate-y-10">
                  <WalletIcon className="w-64 h-64" />
                          </div>
                <div className="relative z-10">
                  <p className="text-slate-400 font-medium mb-1">Total Balance</p>
                  <h2 className="text-4xl font-bold mb-8">₦ {walletBalance.toLocaleString()}</h2>
                  <div className="flex gap-4">
                    <button className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-medium transition text-sm">Withdraw Funds</button>
                    <button className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-xl font-medium transition text-sm">Download Statement</button>
                        </div>
                      </div>
                    </div>

              <h3 className="text-lg font-bold text-slate-900 mt-8">Recent Transactions</h3>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100">
                {filteredTransactions.map(tx => (
                  <div key={tx._id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${['credit', 'deposit'].includes(tx.type) ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {['credit', 'deposit'].includes(tx.type) ? <ArrowDownIcon className="w-5 h-5" /> : <ArrowUpIcon className="w-5 h-5" />}
                      </div>
                              <div>
                        <p className="font-medium text-slate-900 capitalize">{tx.type}</p>
                        <p className="text-xs text-slate-500">{new Date(tx.createdAt).toLocaleDateString()}</p>
                              </div>
                          </div>
                          <div className="text-right">
                      <p className={`font-bold ${['credit', 'deposit'].includes(tx.type) ? 'text-green-600' : 'text-slate-900'}`}>
                        {['credit', 'deposit'].includes(tx.type) ? '+' : '-'} ₦{tx.amount?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-400">{tx.status}</p>
                          </div>
                  </div>
                ))}
                {filteredTransactions.length === 0 && <div className="p-8 text-center text-slate-500">No transactions found</div>}
                        </div>
                      </div>
                    )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="max-w-2xl mx-auto space-y-8">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Account Settings</h2>
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                          <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name</label>
                      <input name="organization" value={profile.organization} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300 focus:ring-emerald-500 focus:border-emerald-500" />
                          </div>
                            <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                      <input name="email" value={profile.email} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300 focus:ring-emerald-500 focus:border-emerald-500" />
                            </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <input name="phoneNumber" value={profile.phoneNumber} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300 focus:ring-emerald-500 focus:border-emerald-500" />
                        </div>
                      </div>
                  <div className="pt-6 border-t border-slate-100">
                    <h3 className="font-semibold text-slate-900 mb-4">Bank Details</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <input name="bank" placeholder="Bank Name" value={profile.bank} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300" />
                      <input name="accountNumber" placeholder="Account Number" value={profile.accountNumber} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300" />
                      <input name="accountName" placeholder="Account Name" value={profile.accountName} onChange={handleProfileChange} className="w-full rounded-lg border-slate-300" />
                  </div>
              </div>
                  <button disabled={loading} className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
                </div>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-slate-900">Tickets</h2>
              <button
                onClick={() => {
                  setEditingTicketType(null)
                  setTicketTypeForm({
                    eventId: '',
                    name: '',
                    rank: '',
                    session: '',
                    price: '',
                    currency: 'NGN',
                    serviceFee: '0',
                    quantity: '',
                    discounts: [],
                    salesWindow: { startsAt: '', endsAt: '' }
                  })
                  setShowTicketTypeForm(true)
                }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                Create Ticket Type
              </button>
            </div>

              {/* Add simplified ticket views here similar to Orders */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ticketTypes.map(tt => (
                  <div key={tt._id || tt.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative group">
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      <button onClick={() => { setEditingTicketType(tt); setShowTicketTypeForm(true) }} className="p-1 bg-slate-100 rounded text-slate-600 hover:text-emerald-600"><PencilIcon className="w-4 h-4" /></button>
                        </div>
                    <h4 className="font-bold text-slate-900">{tt.name}</h4>
                    <p className="text-2xl font-bold text-emerald-600 mt-2">₦{tt.price?.toLocaleString()}</p>
                    <div className="mt-4 flex justify-between text-sm text-slate-500">
                      <span>{tt.quantity - (tt.remaining || 0)} Sold</span>
                      <span>{tt.remaining} Left</span>
                        </div>
                    <div className="w-full bg-slate-100 h-1.5 mt-2 rounded-full overflow-hidden">
                      <div className="bg-emerald-500 h-full" style={{ width: `${((tt.quantity - (tt.remaining || 0)) / tt.quantity) * 100}%` }}></div>
                      </div>
                    </div>
                  ))}
            </div>

              {/* Modal for Ticket Type Form */}
        {showTicketTypeForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">{editingTicketType ? 'Edit Ticket' : 'New Ticket Type'}</h3>
              <form onSubmit={async (e) => {
                e.preventDefault()
                setLoading(true)
                      // ... reusing logic logic inline for brevity ... 
                try {
                  const payload = {
                    ...ticketTypeForm,
                    price: Number(ticketTypeForm.price),
                    serviceFee: Number(ticketTypeForm.serviceFee) || 0,
                    quantity: Number(ticketTypeForm.quantity)
                  }
                        if (editingTicketType) payload.ticketTypeId = editingTicketType._id || editingTicketType.id
                        const res = editingTicketType ? await updateTicketType(payload) : await addTicketType(payload)
                  if (res.status === 200 || res.status === 201) {
                          setSuccess('Ticket saved!')
                    setShowTicketTypeForm(false)
                    fetchTicketTypes()
                  } else {
                          setError('Failed to save')
                  }
                      } catch (err) { setError('Error saving') }
                  setLoading(false)
              }} className="space-y-4">
                <div>
                        <label className="text-sm font-medium text-slate-700">Event</label>
                        <select value={ticketTypeForm.eventId} onChange={e => setTicketTypeForm({ ...ticketTypeForm, eventId: e.target.value })} className="w-full rounded-lg border-slate-300" required>
                          <option value="">Select Event</option>
                          {events.map(ev => <option key={ev._id || ev.id} value={ev._id || ev.id}>{ev.title}</option>)}
                  </select>
                </div>
                      <input placeholder="Ticket Name (e.g. VIP)" value={ticketTypeForm.name} onChange={e => setTicketTypeForm({ ...ticketTypeForm, name: e.target.value })} className="w-full rounded-lg border-slate-300" required />
                      <div className="grid grid-cols-2 gap-4">
                        <input type="number" placeholder="Price" value={ticketTypeForm.price} onChange={e => setTicketTypeForm({ ...ticketTypeForm, price: e.target.value })} className="w-full rounded-lg border-slate-300" required />
                        <input type="number" placeholder="Quantity" value={ticketTypeForm.quantity} onChange={e => setTicketTypeForm({ ...ticketTypeForm, quantity: e.target.value })} className="w-full rounded-lg border-slate-300" required />
                  </div>
                      <div className="flex gap-3 pt-4">
                        <button type="button" onClick={() => setShowTicketTypeForm(false)} className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700">Cancel</button>
                        <button disabled={loading} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg">{loading ? 'Saving...' : 'Save Ticket'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
              </div>
            )}

      </main>
      </div>
    </div>
  )
}

export default CenterDashboardPage
